import { Honeybadger } from '@honeybadger-io/react';

/*
    Helpers for the part of checkout that happens after Stripe has taken the money.

    Two invariants this module exists to protect:

    1. The invoice_tokens we confirm with must be the exact set the PaymentIntent's
       amount was computed from. `checkoutParamsKey` gives us a stable identity for a
       token set so a form can pin one to its intent and notice when it drifts.
    2. Once confirmCardPayment / confirmAcssDebitPayment resolves, the customer has been
       charged. The follow-up PUT that records it on our side is not optional, so it
       retries, it never redirects the page, and it survives a reload.
*/

const CAPTURED_PAYMENT_STORAGE_PREFIX = 'streamlined.captured_payment.';

// Retry schedule in ms; one entry per retry after the initial attempt. Totals ~2.5
// minutes of trying before we fall back to the manual retry UI.
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000, 30000, 30000, 30000];

// Per-attempt ceiling. Deliberately generous: a bulk confirm over many invoices
// legitimately takes several seconds, so a ~10s cutoff would abort healthy requests.
const ATTEMPT_TIMEOUT_MS = 60000;

const RETRYABLE_STATUSES = [408, 425, 429];

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Canonical identity for the set of invoices an amount was computed from. Order
// insensitive, so a reordered-but-identical selection doesn't look like a change.
// Falsy entries are preserved rather than dropped: a token list containing `undefined`
// is a corrupted list and must not compare equal to the clean one.
export const checkoutParamsKey = (checkoutParams) => {
    const {
        invoice_tokens: manyTokens,
        invoice_token: singleToken,
        ...rest
    } = checkoutParams || {};
    const tokens = manyTokens ? [...manyTokens] : (singleToken === undefined ? [] : [singleToken]);
    return JSON.stringify({
        tokens: tokens.map(token => (token === undefined ? null : token)).sort(),
        ...Object.keys(rest).sort().reduce((acc, key) => ({...acc, [key]: rest[key]}), {})
    });
};

// Only the invoice identifiers, stripped of the milestone flags that the confirm
// endpoint doesn't accept. This is what gets pinned to an intent and replayed at record
// time, so it can never be re-derived from a prop that has since changed.
export const toTokenParams = (checkoutParams) => {
    const { invoice_tokens: manyTokens, invoice_token: singleToken } = checkoutParams || {};
    if (manyTokens) return { invoice_tokens: manyTokens };
    if (singleToken !== undefined) return { invoice_token: singleToken };
    return {};
};

const readJsonBody = async (response) => {
    // A 502/504 from the proxy is usually an HTML error page, and a successful record
    // may legitimately return an empty body. Neither should surface to the customer as
    // a JSON parse error.
    let text;
    try {
        text = await response.text();
    } catch {
        return {};
    }
    if (!text) return {};
    try {
        return JSON.parse(text);
    } catch {
        return {};
    }
};

const buildRequestError = ({ message, status, isRetryable, cause, code }) => {
    const error = new Error(message);
    error.status = status;
    error.isRetryable = isRetryable;
    // The backend's machine-readable reason (not_payable, card_unavailable, ...).
    // Absent on network failures, which have no body to carry one.
    if (code) error.code = code;
    if (cause) error.cause = cause;
    return error;
};

/*
    Deliberately does NOT go through utils/errors `handleErrors`. That helper redirects
    the whole page to /503 and signs the user out on a 401 — both catastrophic on a
    payment page, and outright destructive once the card has already been charged.
*/
export const requestCheckoutJson = async (url, { method = 'GET', body } = {}) => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    const timeoutId = controller ? setTimeout(() => controller.abort(), ATTEMPT_TIMEOUT_MS) : undefined;

    let response;
    try {
        response = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            ...(body ? { body: JSON.stringify(body) } : {}),
            ...(controller ? { signal: controller.signal } : {})
        });
    } catch (networkError) {
        // Network failure, DNS, connection reset, or our own abort. Always worth retrying.
        throw buildRequestError({
            message: 'We could not reach the server.',
            isRetryable: true,
            cause: networkError
        });
    } finally {
        if (timeoutId) clearTimeout(timeoutId);
    }

    const data = await readJsonBody(response);

    if (response.ok) return data;

    /*
        `retryable` is the server saying so explicitly, and it outranks the status code
        in both directions. A deposit's record call answers 422 while a confirmed ACH
        payment has not yet surfaced on Stripe's side - terminal by status, transient in
        fact - and the payer was sent to support for a payment that was about to record
        fine. It is only honoured when present: an endpoint that says nothing keeps the
        old status-based behaviour exactly.

        Safe because recording is idempotent per PaymentIntent and guarded at the link,
        so a retry cannot double-record.
    */
    const isRetryable = typeof data?.retryable === 'boolean'
        ? data.retryable
        : response.status >= 500 || RETRYABLE_STATUSES.includes(response.status);

    throw buildRequestError({
        message: data?.message || data?.errors?.[0]?.message || `Request failed with status ${response.status}.`,
        status: response.status,
        code: data?.code,
        isRetryable
    });
};

/*
    Records an already-captured payment, retrying through the transient failures that
    have previously lost settlements (502/504 from the proxy, timeouts, dropped
    connections). Safe to retry because the endpoint is idempotent server-side on
    intent_id.

    onAttemptFailed({attempt, error, willRetry}) fires after each failure so the UI can
    show progress without turning a retryable blip into a hard error.
*/
export const recordCapturedPayment = async ({ url, body, onAttemptFailed, isCancelled }) => {
    const totalAttempts = RETRY_DELAYS_MS.length + 1;
    let lastError;

    for (let attempt = 1; attempt <= totalAttempts; attempt++) {
        if (isCancelled && isCancelled()) return undefined;
        try {
            const data = await requestCheckoutJson(url, { method: 'PUT', body });
            /*
                A recorded payment always comes back as a settlement ({amount, fee, id,
                currency}). An id-less 2xx means the action fell through without rendering:
                the confirm endpoints end in a bare `rescue` that logs and renders nothing,
                which Rails answers with 204 No Content. Taking that as success would clear
                the captured-payment latch and show a confirmation for a payment we never
                recorded, so treat it as a retryable failure instead.
            */
            if (data?.id === undefined || data?.id === null) {
                throw buildRequestError({
                    message: 'The server accepted the request without recording the payment.',
                    isRetryable: true
                });
            }
            return data;
        } catch (error) {
            lastError = error;
            const willRetry = !!error.isRetryable && attempt < totalAttempts;
            onAttemptFailed && onAttemptFailed({ attempt, error, willRetry });
            if (!willRetry) throw error;
            await delay(RETRY_DELAYS_MS[attempt - 1]);
        }
    }

    throw lastError;
};

/*
    A captured-but-unrecorded payment is the highest-severity state this flow can be in:
    the customer's money is gone and we have no record of it. Report the first failure so
    the 502/504 rate is visible even when a retry saves it, and report again — louder —
    if we exhaust every retry.
*/
export const reportUnrecordedPayment = ({ uuid, slug, intentId, attempt, error, exhausted }) => {
    try {
        Honeybadger.notify(
            exhausted
                ? 'Stripe payment captured but NOT recorded after all retries'
                : 'Stripe payment captured but record call failed (retrying)',
            {
                context: {
                    checkoutUuid: uuid,
                    paymentMethod: slug,
                    intentId,
                    attempt,
                    status: error?.status,
                    message: error?.message
                }
            }
        );
    } catch {
        // Never let telemetry failure interfere with the retry loop.
    }
};

/*
    sessionStorage latch. Its job is to make a reload or a reopened tab resume the record
    call instead of offering the customer a fresh payment form — the path that produced
    duplicate charges.
*/
const storageKey = (uuid) => `${CAPTURED_PAYMENT_STORAGE_PREFIX}${uuid}`;

export const rememberCapturedPayment = (uuid, payload) => {
    if (!uuid || typeof window === 'undefined') return;
    try {
        window.sessionStorage.setItem(storageKey(uuid), JSON.stringify(payload));
    } catch {
        // Safari private mode and friends. The in-memory latch still applies.
    }
};

export const readCapturedPayment = (uuid) => {
    if (!uuid || typeof window === 'undefined') return undefined;
    try {
        const stored = window.sessionStorage.getItem(storageKey(uuid));
        return stored ? JSON.parse(stored) : undefined;
    } catch {
        return undefined;
    }
};

export const forgetCapturedPayment = (uuid) => {
    if (!uuid || typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(storageKey(uuid));
    } catch {
        // no-op
    }
};
